import type { Gateway3P } from '../ScopingEditor/useGetState';
import { SVGProps, memo } from 'react';
import { AutoTooltip } from '@/components';
import { ICONS_SPRITE_URL } from '@/components/primitives/icon/Icon';
import { useZoom } from '../ZoomContext';
import { useCanvasColors } from './canvasColors';

export const GATEWAY3P_ICON_SIZE = 1.25;

// Fixed IDs for the single shared pair of gateway3p filters.
// All Gateway3pItem instances reference these instead of creating per-instance <defs>.
const GATEWAY3P_FILTER_NORMAL_ID = 'gateway3p-filter-normal';
const GATEWAY3P_FILTER_SELECTED_ID = 'gateway3p-filter-selected';

/**
 * Renders a single shared <defs> block with the two gateway3p color filters.
 * Mount this once inside the parent SVG so all Gateway3pItem instances can
 * reference the filters by ID without duplicating them.
 */
export function Gateway3pFilters() {
  const colors = useCanvasColors();
  return (
    <defs>
      {/* Normal: recolor icon to zone idle gray */}
      <filter id={GATEWAY3P_FILTER_NORMAL_ID}>
        <feFlood floodColor={colors.roomStrokeIdle} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      {/* Selected: recolor icon using theme-aware stroke color */}
      <filter id={GATEWAY3P_FILTER_SELECTED_ID}>
        <feFlood floodColor={colors.roomStroke} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
    </defs>
  );
}

function Gateway3pItem({
  x = 0,
  y = 0,
  name,
  id,
  selected,
  selecting = false,
  fade = false,
  locked = false,
  onClick,
  ...rest
}: {
  selected: boolean;
  selecting?: boolean;
  fade?: boolean;
  locked?: boolean;
  id: string;
} & SVGProps<SVGGElement> &
  Pick<Gateway3P, 'name'> & { x?: number; y?: number }) {
  const { adjustedZoom } = useZoom();
  const size = GATEWAY3P_ICON_SIZE / adjustedZoom;
  const showAsSelected = selected || selecting;

  return (
    <g
      data-id="gateway3p-item"
      id={id}
      onClick={onClick}
      transform={`translate(${x}, ${y})`}
      style={{ pointerEvents: locked ? 'none' : 'all', opacity: fade ? 0.5 : 1 }}
      data-selected={selected}
      {...rest}
    >
      <AutoTooltip
        label={name}
        placement="top"
        keepOpenOnMouseOver
        forceOpen={selected}
        autoUpdateOnAnimationFrame
      >
        {(props) => (
          <g transform="scale(1, -1)" {...props}>
            {/* pointer-events:none so the <use> bounding box doesn't expand the hit area */}
            <use
              href={`${ICONS_SPRITE_URL}#icon-cloudserveroutlined`}
              width={size}
              height={size}
              x={-size / 2}
              y={-size / 2}
              filter={`url(#${
                showAsSelected ? GATEWAY3P_FILTER_SELECTED_ID : GATEWAY3P_FILTER_NORMAL_ID
              })`}
              pointerEvents="none"
            />
            {/* sole hit target: circle sized to match the visible icon */}
            <circle cx={0} cy={0} r={size * 0.4} fill="transparent" pointerEvents="all" />
          </g>
        )}
      </AutoTooltip>
    </g>
  );
}

export default memo(Gateway3pItem);
